import React, { useState } from 'react';
import { X, Loader2, Building2, Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';
import { blackout } from '../services/api';
import { toast } from '../hooks/use-toast';

const CATEGORIES = [
  'Restaurant', 'Retail', 'Beauty & Grooming', 'Health & Wellness',
  'Professional Services', 'Technology', 'Arts & Entertainment',
  'Nonprofit', 'Education', 'Other',
];

const EMPTY = {
  name: '', category: '', description: '', address: '', city: '', state: '',
  zip: '', website: '', facebook: '', instagram: '', phone: '', email: '',
  contactPerson: '', businessHours: '', businessImage: '', notes: '', sourceUrl: '',
};

const fireConfetti = () => {
  const burst = (opts) => confetti({ spread: 70, origin: { y: 0.6 }, particleCount: 70, ...opts });
  burst({ angle: 60, colors: ['#10b981', '#fbbf24', '#f97316'] });
  burst({ angle: 120, colors: ['#10b981', '#fbbf24', '#f97316'] });
  setTimeout(() => burst({ particleCount: 100, spread: 100, startVelocity: 55 }), 250);
};

export default function BlackoutSubmitBusinessModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {status, message, pointsAwarded}

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: 'Business name is required' }); return; }
    setBusy(true);
    try {
      const res = await blackout.submitBusiness(form);
      setResult(res);
      if (res.status === 'new') fireConfetti();
      onSuccess?.(res);
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setForm(EMPTY); setResult(null); onClose(); };

  if (result) {
    const palette = {
      new: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
      duplicate: 'border-amber-500/60 bg-amber-500/10 text-amber-200',
      pending_review: 'border-sky-500/60 bg-sky-500/10 text-sky-200',
      already_credited: 'border-neutral-500/40 bg-neutral-500/10 text-neutral-200',
    }[result.status] || 'border-neutral-500/40';
    return (
      <ModalShell onClose={close} title="Submission Result" testId="blackout-biz-result">
        <div className={`rounded-xl border-2 ${palette} p-6 text-center`}>
          <Trophy className="mx-auto mb-3 text-amber-300" size={40}/>
          <div className="text-xl font-black mb-2 uppercase tracking-wider">{result.message}</div>
          {result.pointsAwarded > 0 && (
            <div className="text-3xl font-black text-amber-300 mt-3" data-testid="blackout-biz-points-awarded">
              +{result.pointsAwarded} points
            </div>
          )}
          {result.flagReason && (
            <div className="mt-3 text-xs text-neutral-400">Flag: {result.flagReason.replace(/_/g,' ')}</div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => { setResult(null); setForm(EMPTY); }} data-testid="blackout-biz-submit-another"
            className="px-4 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-200 hover:border-emerald-500">
            Submit another
          </button>
          <button onClick={close} data-testid="blackout-biz-close-result"
            className="px-4 py-2 rounded-lg bg-emerald-500 text-black font-semibold text-sm">Done</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={close} title="Add a Business" icon={Building2} testId="blackout-biz-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Business Name *" value={form.name} onChange={set('name')} testId="biz-name"/>
        <SelectField label="Category" value={form.category} onChange={set('category')} options={CATEGORIES} testId="biz-category"/>
        <Field label="Address" value={form.address} onChange={set('address')} testId="biz-address"/>
        <Field label="City" value={form.city} onChange={set('city')} testId="biz-city"/>
        <Field label="State" value={form.state} onChange={set('state')} testId="biz-state"/>
        <Field label="ZIP" value={form.zip} onChange={set('zip')} testId="biz-zip"/>
        <Field label="Website" value={form.website} onChange={set('website')} testId="biz-website"/>
        <Field label="Phone" value={form.phone} onChange={set('phone')} testId="biz-phone"/>
        <Field label="Email" value={form.email} onChange={set('email')} testId="biz-email"/>
        <Field label="Contact Person" value={form.contactPerson} onChange={set('contactPerson')} testId="biz-contact"/>
        <Field label="Facebook" value={form.facebook} onChange={set('facebook')} testId="biz-fb"/>
        <Field label="Instagram" value={form.instagram} onChange={set('instagram')} testId="biz-ig"/>
        <Field label="Business Hours" value={form.businessHours} onChange={set('businessHours')} testId="biz-hours"/>
        <Field label="Source URL (where you found it)" value={form.sourceUrl} onChange={set('sourceUrl')} testId="biz-source"/>
      </div>
      <Textarea label="Description" value={form.description} onChange={set('description')} testId="biz-desc"/>
      <Textarea label="Additional Notes" value={form.notes} onChange={set('notes')} testId="biz-notes"/>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-neutral-800">
        <button onClick={close} className="px-4 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-300">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="blackout-biz-submit"
          className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={14}/> : null}
          Submit Discovery
        </button>
      </div>
    </ModalShell>
  );
}

export function ModalShell({ onClose, title, icon: Icon, testId, children }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid={testId}>
      <div className="w-full max-w-3xl bg-[#0a0a0a] border border-neutral-800 rounded-2xl my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800 sticky top-0 bg-[#0a0a0a] rounded-t-2xl">
          <div className="font-bold text-white flex items-center gap-2">
            {Icon && <Icon size={18} className="text-emerald-400"/>} {title}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={20}/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{label}</span>
      <input value={value} onChange={onChange} data-testid={testId}
        className="mt-1 w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none"/>
    </label>
  );
}
export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{label}</span>
      <select value={value} onChange={onChange} data-testid={testId}
        className="mt-1 w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none">
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
export function Textarea({ label, value, onChange, testId }) {
  return (
    <label className="block mt-3">
      <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{label}</span>
      <textarea value={value} onChange={onChange} data-testid={testId} rows={3}
        className="mt-1 w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none resize-y"/>
    </label>
  );
}
